<template>
  <aside
    class="bg-light text-dark shadow border-right component-menubar-sidebar transition-all"
    :style="sidebarStyle"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @touchstart.stop="handleTouchStart"
    @click.stop
  >
    <router-link
      :to="{ name: 'Clinic' }"
      class="navbar-brand text-dark text-decoration-none d-block p-3"
    >
      <!-- The Dentistry: brandmark when collapsed, wordmark when expanded —
           never both at once (identity manual p11). -->
      <template v-if="isCiTenant">
        <img
          v-if="effectivelyCollapsed"
          src="@/assets/brand/brandmark.svg"
          alt="The Dentistry"
          class="d-inline-block brand-brandmark"
        />
        <img
          v-else
          src="@/assets/brand/wordmark.svg"
          alt="The Dentistry"
          class="d-inline-block brand-wordmark"
        />
      </template>
      <template v-else>
        <img
          src="@/assets/img/dentcloud-logo-darkblue.png"
          alt="Pastel Logo"
          class="d-inline-block"
          style="height: 30px"
          :style="{ marginRight: effectivelyCollapsed ? '0' : '0.5rem' }"
        />
        <span
          v-show="showText"
          class="brand-text"
          >DentCloud</span
        >
      </template>
    </router-link>
    <div class="">
      <nav class="mt-2">
        <b-nav
          v-if="hasClinicBranchUrl()"
          vertical
          class="flex-column"
        >
          <b-nav-item
            v-if="isAccessMenu([ROLES.DOCTOR])"
            :to="{ name: 'Dashboard' }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'Dashboard' }"
            :title="effectivelyCollapsed ? 'ปฏิทินแพทย์' : ''"
          >
            <i
              class="fas fa-user-md"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">ปฏิทินแพทย์</span>
          </b-nav-item>

          <b-nav-item
            v-if="isAccessMenu([ROLES.ASSISTANT])"
            :to="{ name: 'DoctorsTreatingQueue' }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'DoctorsTreatingQueue' }"
            :title="effectivelyCollapsed ? 'ปฏิทินแพทย์ทุกท่าน' : ''"
          >
            <i
              class="far fa-calendar-plus"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">ปฏิทินแพทย์ทุกท่าน</span>
          </b-nav-item>

          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.ASSISTANT,
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.HEADCOUNTER,
              ])
            "
            :to="{ name: 'QueueManage' }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'QueueManage' }"
            :title="effectivelyCollapsed ? 'ปฏิทินนัดหมาย' : ''"
          >
            <i
              class="far fa-calendar-alt"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">ปฏิทินนัดหมาย</span>
          </b-nav-item>

          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.ASSISTANT,
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.HEADCOUNTER,
              ])
            "
            :to="{ name: 'QueueList' }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'QueueList' }"
            :title="effectivelyCollapsed ? 'รายการนัดหมาย' : ''"
          >
            <i
              class="far fa-calendar-minus"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">รายการนัดหมาย</span>
          </b-nav-item>

          <!-- Lists/Package - Simple Collapsed Icon -->
          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.DOCTOR,
                ROLES.HEADCOUNTER,
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'ลิสต์/แพ็กเกจจัดฟัน'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'packagemanage',
                'aptlistmanage',
                'txlistmanage',
                'dxlistmanage',
                'itemslistmanage',
                'labproductmanage',
              ]),
            }"
          >
            <i class="fas fa-list"></i>
          </b-nav-item>

          <!-- Lists/Package Dropdown - Expanded -->
          <div
            v-if="
              isAccessMenu([
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.DOCTOR,
                ROLES.HEADCOUNTER,
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-lists
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'packagemanage',
                  'aptlistmanage',
                  'txlistmanage',
                  'dxlistmanage',
                  'itemslistmanage',
                ]),
              }"
            >
              <span>
                <i class="fas fa-list mr-2"></i>
                <span v-show="showText">ลิสต์/แพ็กเกจจัดฟัน</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-lists"
              class="ml-3"
            >
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'PackageManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'PackageManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">แพ็กเกจจัดฟัน</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'AptListManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'AptListManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ลิสต์นัดหมาย</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                    ROLES.ROLE_BRANCH_STOCK_MANAGER,
                    ROLES.ROLE_STOCK_STAFF,
                  ])
                "
                :to="{ name: 'TxListManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'TxListManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ลิสต์การรักษา</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'DxListManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'DxListManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ลิสต์การวินิจฉัย</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                    ROLES.ROLE_BRANCH_STOCK_MANAGER,
                    ROLES.ROLE_STOCK_STAFF,
                  ])
                "
                :to="{ name: 'ItemsListManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'ItemsListManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ลิสต์ยา และสินค้า</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'LabProductManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'LabProductManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ลิสต์แลป</span>
              </b-nav-item>
            </b-collapse>
          </div>

          <!-- ----------finance---------- -->
          <b-nav-item
            v-if="
              isAccessMenu([ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'การเงิน'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'ReceiptManage',
                'DFCorrection',
                'DiscountManagement',
                'PatientGroupManagement',
                'LabExpense',
                'VoucherManagement',
                'VoucherBatchReport',
                'TierManagement',
                'TierReport',
              ]),
            }"
          >
            <i class="fas fa-money-bill-wave"></i>
          </b-nav-item>

          <div
            v-if="
              isAccessMenu([ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-finance
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'ReceiptManage',
                  'DFCorrection',
                  'DiscountManagement',
                  'PatientGroupManagement',
                  'LabExpense',
                  'VoucherManagement',
                  'VoucherBatchReport',
                  'TierManagement',
                  'TierReport',
                ]),
              }"
            >
              <span>
                <i class="fas fa-money-bill-wave mr-2"></i>
                <span v-show="showText">การเงิน</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-finance"
              class="ml-3"
            >
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'ReceiptManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'ReceiptManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการใบเสร็จ</span>
              </b-nav-item>
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER])"
                :to="{ name: 'DFCorrection' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'DFCorrection' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">แก้ไข DF</span>
              </b-nav-item>
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER])"
                :to="{ name: 'DiscountManagement' }"
                class="text-dark py-0"
                :class="{
                  'bg-secondary': $route.name === 'DiscountManagement',
                }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการส่วนลด</span>
              </b-nav-item>
              <b-nav-item
                v-if="canViewVouchers"
                :to="{ name: 'VoucherManagement' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'VoucherManagement' }"
              >
                <i class="fas fa-circle mr-2" style="font-size: 0.5rem"></i>
                <span v-show="showText">จัดการบัตรกำนัล</span>
              </b-nav-item>
              <!-- ระดับลูกค้า (patient tier) menu hidden — routes remain accessible by URL
              <b-nav-item
                v-if="canManageTiers"
                :to="{ name: 'TierManagement' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'TierManagement' }"
              >
                <i class="fas fa-circle mr-2" style="font-size: 0.5rem"></i>
                <span v-show="showText">จัดการระดับลูกค้า</span>
              </b-nav-item>
              <b-nav-item
                v-if="canViewClinicReport"
                :to="{ name: 'TierReport' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'TierReport' }"
              >
                <i class="fas fa-circle mr-2" style="font-size: 0.5rem"></i>
                <span v-show="showText">รายงานระดับลูกค้า</span>
              </b-nav-item>
              -->
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER])"
                :to="{ name: 'PatientGroupManagement' }"
                class="text-dark py-0"
                :class="{
                  'bg-secondary': $route.name === 'PatientGroupManagement',
                }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการกลุ่มผู้ป่วย</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'LabExpense' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'LabExpense' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการค่าแลป</span>
              </b-nav-item>
            </b-collapse>
          </div>
          <!-- ----------finance---------- -->

          <!-- ----------PR & PO---------- -->
          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'จัดการจัดซื้อ'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'PurchaseRequest',
                'PurchaseOrder',
              ]),
            }"
          >
            <span class="nav-icon-wrapper">
              <i class="fas fa-shopping-cart"></i>
              <span
                v-if="sidebarRedDot"
                class="nav-red-dot"
              ></span>
            </span>
          </b-nav-item>

          <!-- ----------product and stock---------- -->
          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'จัดการสินค้าคงคลัง'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'unitMaster',
                'GoodsReceive',
                'ProductInventory',
                'TransferInventory',
                'CategoryMaster',
                'StockManagement',
              ]),
            }"
          >
            <span class="nav-icon-wrapper">
              <i class="fas fa-boxes"></i>
              <span
                v-if="showProductStockRedDot"
                class="nav-red-dot"
              ></span>
            </span>
          </b-nav-item>

          <!-- PR & PO -->
          <div
            v-if="
              isAccessMenu([
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-prpo-management
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'PurchaseRequest',
                  'PurchaseOrder',
                ]),
              }"
            >
              <span>
                <i class="fas fa-shopping-cart mr-2 nav-icon-wrapper">
                  <span
                    v-if="sidebarRedDot"
                    class="nav-red-dot"
                  ></span>
                </i>
                <span v-show="showText">จัดการจัดซื้อ</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-prpo-management"
              class="ml-3"
            >
              <!-- ProductInventory -->
              <b-nav-item
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'PurchaseRequest' }"
                @click="goToPurchaseRequest"
              >
                <i
                  class="fas fa-circle mr-2 nav-icon-wrapper"
                  style="font-size: 0.5rem"
                >
                  <span
                    v-if="sidebarRedDot"
                    class="nav-red-dot"
                  ></span>
                </i>
                <span v-show="showText">ใบขอซื้อ (PR)</span>
              </b-nav-item>

              <!-- GoodsReceive -->
              <b-nav-item
                :to="{ name: 'PurchaseOrder' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'PurchaseOrder' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ใบสั่งซื้อ (PO)</span>
              </b-nav-item>
            </b-collapse>
          </div>

          <div
            v-if="
              isAccessMenu([
                ROLES.ROLE_BRANCH_STOCK_MANAGER,
                ROLES.ROLE_STOCK_STAFF,
              ]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-product-management
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'unitMaster',
                  'GoodsReceive',
                  'ProductInventory',
                  'TransferInventory',
                  'CategoryMaster',
                  'StockManagement',
                ]),
              }"
            >
              <span>
                <i class="fas fa-boxes mr-2 nav-icon-wrapper">
                  <span
                    v-if="showProductStockRedDot"
                    class="nav-red-dot"
                  ></span>
                </i>
                <span v-show="showText">จัดการสินค้าคงคลัง</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-product-management"
              class="ml-3"
            >
              <!-- ProductInventory -->
              <b-nav-item
                :to="{ name: 'ProductInventory' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'ProductInventory' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">สินค้าและสต๊อก</span>
              </b-nav-item>

              <!-- ProductInventory -->
              <b-nav-item
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'StockManagement' }"
                @click="goToStockManagement"
              >
                <i
                  class="fas fa-circle mr-2 nav-icon-wrapper"
                  style="font-size: 0.5rem"
                >
                  <span
                    v-if="stockAdjRedDot"
                    class="nav-red-dot"
                  ></span>
                </i>
                <span v-show="showText">จัดการสต๊อก</span>
              </b-nav-item>

              <!-- GoodsReceive -->
              <b-nav-item
                :to="{ name: 'GoodsReceive' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'GoodsReceive' }"
              >
                <i
                  class="fas fa-circle mr-2 nav-icon-wrapper"
                  style="font-size: 0.5rem"
                >
                  <span
                    v-if="grRedDot"
                    class="nav-red-dot"
                  ></span>
                </i>
                <span v-show="showText">ใบรับสินค้า</span>
              </b-nav-item>

              <!-- TransferInventory -->
              <b-nav-item
                :to="{ name: 'TransferInventory' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'TransferInventory' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ใบโอนสินค้า</span>
              </b-nav-item>

              <!-- Category Master -->
              <b-nav-item
                :to="{ name: 'CategoryMaster' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'CategoryMaster' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ตั้งค่าหมวดหมู่สินค้า</span>
              </b-nav-item>

              <!-- UnitMaster -->
              <b-nav-item
                :to="{ name: 'UnitMaster' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'UnitMaster' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ตั้งค่าหน่วยสินค้า</span>
              </b-nav-item>
            </b-collapse>
          </div>

          <!-- ----------product and stock---------- -->

          <!-- Reports - Simple Collapsed Icon -->
          <b-nav-item
            v-if="
              isAccessMenu([
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.DOCTOR,
                ROLES.HEADCOUNTER,
              ]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'รายงาน'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'BranchDashboard',
                'personalincome',
                'branchincome',
              ]),
            }"
          >
            <i class="fas fa-chart-bar"></i>
          </b-nav-item>

          <!-- Reports Dropdown - Expanded -->
          <div
            v-if="
              isAccessMenu([
                ROLES.COUNTER,
                ROLES.MANAGER,
                ROLES.DOCTOR,
                ROLES.HEADCOUNTER,
              ]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-reports
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'BranchDashboard',
                  'personalincome',
                  'branchincome',
                ]),
              }"
            >
              <span>
                <i class="fas fa-chart-bar mr-2"></i>
                <span v-show="showText">รายงาน</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse id="collapse-reports" class="ml-3">
              <b-nav-item
                v-if="isAccessMenu(['MANAGER'])"
                :to="{ name: 'BranchDashboard' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'BranchDashboard' }"
              >
                <i class="fas fa-circle mr-2" style="font-size: 0.5rem"></i>
                <span v-show="showText">แดชบอร์ด</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.DOCTOR,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'PersonalIncome' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'PersonalIncome' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">รายได้แพทย์</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.COUNTER,
                    ROLES.MANAGER,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'BranchIncome' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'BranchIncome' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">รายงานสาขา</span>
              </b-nav-item>
            </b-collapse>
          </div>

          <!-- AI Analysis - Simple Collapsed Icon -->
          <b-nav-item
            v-if="
              isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'AI Analysis (Beta)'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'branchanalysis',
                'patientanalysis',
              ]),
            }"
          >
            <i class="fas fa-robot"></i>
          </b-nav-item>

          <!-- AI Analysis Dropdown - Expanded -->
          <div
            v-if="
              isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-ai
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'branchanalysis',
                  'patientanalysis',
                ]),
              }"
            >
              <span>
                <i class="fas fa-robot mr-2"></i>
                <span v-show="showText">AI Analysis (Beta)</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-ai"
              class="ml-3"
            >
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER])"
                :to="{ name: 'branchAnalysis' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'branchAnalysis' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">รายได้</span>
              </b-nav-item>
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER])"
                :to="{ name: 'PatientAnalysis' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'PatientAnalysis' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ผู้รับบริการ</span>
              </b-nav-item>
              <!-- <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER])"
                disabled
                class="text-muted py-1"
              >
                <i class="fas fa-circle mr-2" style="font-size: 0.5rem"></i>
                <span v-show="showText">นัดหมาย</span>
              </b-nav-item> -->
            </b-collapse>
          </div>

          <!-- Settings - Simple Collapsed Icon -->
          <b-nav-item
            v-if="
              isAccessMenu([ROLES.MANAGER, ROLES.COUNTER, ROLES.HEADCOUNTER]) &&
              effectivelyCollapsed &&
              !isMobile
            "
            class="text-dark"
            :title="'ตั้งค่า'"
            :class="{
              'bg-secondary': checkActiveMenu([
                'setting',
                'subscription',
                'UserManage',
                'DoctorManage',
                'FileManage',
              ]),
            }"
          >
            <i class="fas fa-cog"></i>
          </b-nav-item>

          <!-- Settings Dropdown - Expanded -->
          <div
            v-if="
              isAccessMenu([ROLES.MANAGER, ROLES.COUNTER, ROLES.HEADCOUNTER]) &&
              (!effectivelyCollapsed || (isMobile && mobileOpen))
            "
          >
            <b-button
              v-b-toggle.collapse-settings
              variant="link"
              class="text-dark text-decoration-none d-flex align-items-center justify-content-between w-100 p-2 px-3"
              :class="{
                'bg-secondary': checkActiveMenu([
                  'setting',
                  'subscription',
                  'UserManage',
                  'DoctorManage',
                  'FileManage',
                ]),
              }"
            >
              <span>
                <i class="fas fa-cog mr-2"></i>
                <span v-show="showText">ตั้งค่า</span>
              </span>
              <i class="fas fa-angle-down"></i>
            </b-button>
            <b-collapse
              id="collapse-settings"
              class="ml-3"
            >
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.MANAGER,
                    ROLES.COUNTER,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'UserManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'UserManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการผู้ใช้</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([
                    ROLES.MANAGER,
                    ROLES.COUNTER,
                    ROLES.HEADCOUNTER,
                  ])
                "
                :to="{ name: 'DoctorManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'DoctorManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการแพทย์</span>
              </b-nav-item>
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER])"
                :to="{ name: 'Setting' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'Setting' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">ตั้งค่าสาขา</span>
              </b-nav-item>
              <b-nav-item
                v-if="
                  isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER]) &&
                  getBranchInfo.fileStoreExpiration
                "
                :to="{ name: 'FileManage' }"
                class="text-dark py-0"
                :class="{ 'bg-secondary': $route.name === 'FileManage' }"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">จัดการไฟล์</span>
              </b-nav-item>
              <b-nav-item
                v-if="isAccessMenu([ROLES.MANAGER, ROLES.HEADCOUNTER])"
                :to="{ name: 'Subscription' }"
                :disabled="isProductionEnvironment"
                :class="[
                  'py-1',
                  { 'disabled-menu-item': isProductionEnvironment },
                ]"
              >
                <i
                  class="fas fa-circle mr-2"
                  style="font-size: 0.5rem"
                ></i>
                <span v-show="showText">การสมัครบริการ</span>
              </b-nav-item>
            </b-collapse>
          </div>

          <!-- ทางลัดกลับไประดับคลินิก — เดิมต้องผ่าน dropdown ผู้ใช้ → เลือกคลินิก/สาขา
               → การ์ด "ระดับคลินิก" รวม 3 คลิก -->
          <b-nav-item
            v-if="canEnterCentral"
            :to="{ name: 'ClinicCentral', params: { clinicUrl: currentClinicUrl } }"
            class="text-dark"
            :title="effectivelyCollapsed ? 'ระดับคลินิก' : ''"
          >
            <i class="fas fa-building" :class="{ 'mr-2': !effectivelyCollapsed }"></i>
            <span v-show="showText">ระดับคลินิก</span>
          </b-nav-item>
        </b-nav>

        <b-nav
          v-else-if="isCentralRoute()"
          vertical
        >
          <b-nav-item
            v-if="canViewClinicDashboard"
            :to="{
              name: 'ClinicDashboard',
              params: { clinicUrl: currentClinicUrl },
            }"
            class="text-dark"
            :class="{ 'bg-secondary': checkActiveMenu(['ClinicDashboard', 'CentralBranchDashboard']) }"
            :title="effectivelyCollapsed ? clinicDashboardMenuLabel : ''"
          >
            <i
              class="fas fa-chart-line"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">{{ clinicDashboardMenuLabel }}</span>
          </b-nav-item>
          <b-nav-item
            v-if="canViewClinicReport"
            :to="{ name: 'ClinicIncome', params: { clinicUrl: currentClinicUrl } }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'ClinicIncome' }"
            :title="effectivelyCollapsed ? 'รายได้คลินิก' : ''"
          >
            <i
              class="fas fa-chart-bar"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">รายได้คลินิก</span>
          </b-nav-item>
          <b-nav-item
            v-if="canManageAiAssistant"
            :to="{ name: 'ClinicAiAssistant', params: { clinicUrl: currentClinicUrl } }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'ClinicAiAssistant' }"
            :title="effectivelyCollapsed ? 'AI Assistant' : ''"
          >
            <i
              class="fas fa-robot"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">AI Assistant</span>
          </b-nav-item>
          <b-nav-item
            v-if="canViewClinic"
            :to="{
              name: 'ClinicManagement',
              params: { clinicUrl: currentClinicUrl },
            }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'ClinicManagement' }"
            :title="effectivelyCollapsed ? 'ผู้ใช้และสิทธิ์' : ''"
          >
            <i
              class="fas fa-users"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">ผู้ใช้และสิทธิ์</span>
          </b-nav-item>
          <b-nav-item
            v-if="canHqApprove"
            :to="{
              name: 'HQApproval',
              params: { clinicUrl: currentClinicUrl },
            }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'HQApproval' }"
            :title="effectivelyCollapsed ? 'การอนุมัติ' : ''"
          >
            <span
              class="nav-icon-wrapper"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            >
              <svg
                width="18"
                height="20"
                viewBox="0 0 18 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.3125 2.91667H11.6667C11.6667 1.30794 10.3587 0 8.75 0C7.14128 0 5.83333 1.30794 5.83333 2.91667H2.1875C0.979818 2.91667 0 3.89648 0 5.10417V21.1458C0 22.3535 0.979818 23.3333 2.1875 23.3333H15.3125C16.5202 23.3333 17.5 22.3535 17.5 21.1458V5.10417C17.5 3.89648 16.5202 2.91667 15.3125 2.91667ZM8.75 1.82292C9.35612 1.82292 9.84375 2.31055 9.84375 2.91667C9.84375 3.52279 9.35612 4.01042 8.75 4.01042C8.14388 4.01042 7.65625 3.52279 7.65625 2.91667C7.65625 2.31055 8.14388 1.82292 8.75 1.82292ZM14.2734 12.3867L7.75651 18.849C7.54232 19.0632 7.19596 19.0586 6.98177 18.8444L3.21745 15.0482C3.00326 14.834 3.00781 14.4876 3.22201 14.2734L4.51628 12.9883C4.73047 12.7741 5.07682 12.7786 5.29102 12.9928L7.38737 15.1074L12.2181 10.3132C12.4323 10.099 12.7786 10.1035 12.9928 10.3177L14.278 11.612C14.4922 11.8307 14.4876 12.1725 14.2734 12.3867Z"
                  fill="currentColor"
                />
              </svg>
              <span
                v-if="hqApprovalRedDot"
                class="nav-red-dot"
              ></span>
            </span>
            <span v-show="showText">การอนุมัติ</span>
          </b-nav-item>
          <b-nav-item
            v-if="canExportReportHQ"
            :to="{
              name: 'ReportDownloadHQ',
              params: { clinicUrl: currentClinicUrl },
            }"
            class="text-dark"
            :class="{ 'bg-secondary': $route.name === 'ReportDownloadHQ' }"
            :title="effectivelyCollapsed ? 'การดาวน์โหลดรายงาน' : ''"
          >
            <i
              class="fas fa-file-download"
              :class="{ 'mr-2': !effectivelyCollapsed }"
            ></i>
            <span v-show="showText">การดาวน์โหลดรายงาน</span>
          </b-nav-item>
        </b-nav>
      </nav>
      <!-- Version number at the bottom of sidebar -->
      <div
        v-show="showText"
        class="version-info text-center mt-2 mb-2"
      >
        <small class="text-muted">v{{ version }}</small>
      </div>
      <!-- White-label attribution (renders only for non-DentCloud tenants) -->
      <div v-show="showText" class="sidebar-powered-by text-center mb-3">
        <PoweredByDentCloud compact />
      </div>
    </div>
  </aside>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import { ROLES } from "@/constants/user";
import { PERMISSIONS } from "@/constants/roles";
import { resolveTenant } from "@/config/tenant";
import PoweredByDentCloud from "@/components/brand/PoweredByDentCloud";

const TENANT = resolveTenant({ VUE_APP_TENANT: process.env.VUE_APP_TENANT });

export default {
  components: { PoweredByDentCloud },
  name: "Sidebar",

  data() {
    return {
      isCiTenant: TENANT.isCi,
      ROLES,
      // Sidebar state
      sidebarCollapsed: true,
      isTemporarilyExpanded: false,
      mobileOpen: false,

      // Text visibility
      showText: false,
      textDelayTimer: null,

      // Auto-collapse functionality
      autoCollapseTimer: null,

      // Hover expand functionality
      hoverExpandTimer: null,

      // Responsive behavior
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024,

      // App info
      authId: 0,
      version:
        process.env.VUE_APP_VERSION || process.env.PACKAGE_VERSION || "1.0.0",
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      authorization: "moduleUser/getAuthorization",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getBranchInfo: "moduleUser/getBranchInfo",
      isClinicWideDashboardViewer: "moduleUser/isClinicWideDashboardViewer",
      getClinicList: "moduleUser/getClinicList",
      can: "moduleUser/can",
      isShowRedDot: "moduleMrp/isShowRedDot",
      isPendingRedDot: "modulePurchaseRequest/isPendingRedDot",
      isGrWaitingBmRedDot: "moduleGoodsReceive/isGrWaitingBmRedDot",
      isGrRejectedRedDot: "moduleGoodsReceive/isGrRejectedRedDot",
      isGrWaitingHqRedDot: "moduleGoodsReceive/isGrWaitingHqRedDot",
      isWaitingBmRedDot: "moduleProductInventory/isWaitingBmRedDot",
      isWaitingHqRedDot: "moduleProductInventory/isWaitingHqRedDot",
      getPermissionContext: "moduleUser/getPermissionContext",
    }),

    stockAdjRedDot() {
      return (
        this.isWaitingBmRedDot &&
        this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER])
      );
    },
    grRedDot() {
      if (this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER])) {
        return this.isGrWaitingBmRedDot;
      } else if (this.isAccessMenu([ROLES.ROLE_STOCK_STAFF])) {
        return this.isGrRejectedRedDot;
      }
      return false;
    },
    showProductStockRedDot() {
      return this.stockAdjRedDot || this.grRedDot;
    },
    // Central "การอนุมัติ" (HQApproval) dot: stock adjustment OR GR waiting for HQ.
    hqApprovalRedDot() {
      return this.isWaitingHqRedDot || this.isGrWaitingHqRedDot;
    },

    // Sidebar red dot: shown on the PR menu when the current role has something actionable.
    // ROLE_BRANCH_STOCK_MANAGER sees dot when there are pending PRs to approve.
    // ROLE_STOCK_STAFF see dot when there are MRP entries to process.
    sidebarRedDot() {
      if (this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER])) {
        return this.isPendingRedDot;
      } else if (this.isAccessMenu([ROLES.ROLE_STOCK_STAFF])) {
        return this.isShowRedDot;
      }
      return false;
    },

    // Phase 1C: central-mode permission helpers.
    currentClinicUrl() {
      return (
        this.$route.params.clinicUrl ||
        this.getBranchInfo?.clinic?.code ||
        this.getClinicList?.[0]?.code ||
        null
      );
    },
    canViewClinic() {
      if (!this.getPermissionContext && this.isCentralRoute()) return true;
      return this.can(PERMISSIONS.USER_VIEW_CLINIC);
    },
    canHqApprove() {
      if (!this.getPermissionContext && this.isCentralRoute()) return true;
      return this.can(PERMISSIONS.STOCK_HQ_APPROVE);
    },
    canExportReportHQ() {
      if (!this.getPermissionContext && this.isCentralRoute()) return true;
      return this.can(PERMISSIONS.STOCK_EXPORT_REPORT_HQ);
    },
    canViewClinicDashboard() {
      // dashboard.clinic.view now covers both C_LEVEL and RDM, not C-level only.
      // `can` still returns false until the permission context loads, so the menu
      // stays hidden (rather than flashing in) until that context is confirmed.
      return this.can(PERMISSIONS.DASHBOARD_CLINIC_VIEW);
    },
    canManageAiAssistant() {
      // mcp.setting.manage = C_LEVEL only (standalone managers use the Setting tab).
      return this.can(PERMISSIONS.MCP_SETTING_MANAGE);
    },
    canViewClinicReport() {
      // report.clinic.view = executives + finance (accountant). Hidden until the
      // permission context loads, same as the clinic dashboard link above.
      return this.can(PERMISSIONS.REPORT_CLINIC_VIEW);
    },
    // voucher.view spans branch roles (MANAGER/HEADCOUNTER) AND central roles
    // (RDM/C_LEVEL/ACCOUNTANT). isAccessMenu cannot express that — it maps names
    // through a table of the seven branch roles only, so every central role
    // silently became undefined and an ACCOUNTANT, one of the three roles that
    // may actually mint a batch, saw no menu entry at all. clinicId only (no
    // branchId): batches are clinic-scoped, matching the BE's own ctx.
    canViewVouchers() {
      const clinicId = this.getBranchInfo?.clinic?.id;
      return this.can(PERMISSIONS.VOUCHER_VIEW, { clinicId });
    },

    // tier.manage is central-only (C_LEVEL/ACCOUNTANT). The route itself
    // still lives under the branch URL (/:clinicUrl/:branchUrl/tiermanage) —
    // a central user reaches it while looking at one branch's context, same
    // as the voucher entry above — but the permission it gates is
    // clinic-scoped, so clinicId only, matching canViewVouchers.
    canManageTiers() {
      const clinicId = this.getBranchInfo?.clinic?.id;
      return this.can(PERMISSIONS.TIER_MANAGE, { clinicId });
    },

    // ตรงกับสองปลายทางที่ CentralEntry ส่งไปได้จริง: ClinicDashboard หรือ ClinicManagement
    // ใครที่ไม่เข้าเงื่อนไขสักอันจะถูกเด้งกลับ /clinic จึงไม่ควรเห็นลิงก์ตั้งแต่แรก
    canEnterCentral() {
      return (
        this.can(PERMISSIONS.DASHBOARD_CLINIC_VIEW) ||
        this.can(PERMISSIONS.USER_VIEW_CLINIC) ||
        this.can(PERMISSIONS.REPORT_CLINIC_VIEW)
      );
    },

    // ผู้ใช้ที่ถูกจำกัดขอบเขตเห็นแค่บางสาขา คำว่า "ภาพรวมคลินิก" จึงเกินจริงสำหรับพวกเขา
    clinicDashboardMenuLabel() {
      return this.isClinicWideDashboardViewer ? "ภาพรวมคลินิก" : "สาขาที่ดูแล";
    },

    // Device detection
    isTouchDevice() {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },

    // Environment detection
    isProductionEnvironment() {
      return process.env.VUE_APP_MODE === "production";
    },

    // Sidebar state
    effectivelyCollapsed() {
      return this.sidebarCollapsed && !this.isTemporarilyExpanded;
    },
    sidebarStyle() {
      const isMobile = this.isMobile;

      let transform = "";
      let zIndex;

      if (isMobile) {
        // Mobile mode: use transform for show/hide, always high z-index
        transform = this.mobileOpen ? "translateX(0)" : "translateX(-100%)";
        zIndex = 1000; // Always high z-index on mobile
      } else {
        // Desktop mode: no transform, z-index based on expansion state
        transform = "";
        zIndex = this.effectivelyCollapsed ? "auto" : 1000;
      }

      // Width calculation
      let width;
      if (isMobile) {
        width = "250px"; // Always full width on mobile
      } else {
        width = this.effectivelyCollapsed ? "60px" : "250px";
      }

      const style = {
        width: width,
        height: "100vh",
        position: "fixed",
        left: "0",
        top: "0",
        zIndex: zIndex,
        transform: transform,
        transition:
          "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
      };

      return style;
    },
  },
  created() {
    this.authId = this.authorization.authId;
    // Auto-collapse sidebar on clinic path
    this.checkRouteForCollapse();
    const clinicUrl = this.$route.params.clinicUrl;
    const branchUrl = this.$route.params.branchUrl;
    if (clinicUrl && branchUrl) {
      if (
        this.isAccessMenu([
          ROLES.ROLE_STOCK_STAFF,
          ROLES.ROLE_BRANCH_STOCK_MANAGER,
        ])
      ) {
        this.fetchMrpList({ clinicUrl, branchUrl });
        this.fetchGoodsReceiveList({ clinicUrl, branchUrl });
      }
      if (this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER])) {
        this.fetchPurchaseRequests({ clinicUrl, branchUrl });
        this.fetchStockAdjustmentList({ clinicUrl, branchUrl });
      }
    }
    if (clinicUrl && this.canHqApprove) {
      this.fetchHQStockAdjustmentList({ clinicUrl });
    }
  },
  mounted() {
    this.initializeEventListeners();
    this.initializeMobileStateObserver();
    this.initializeState();
  },
  watch: {
    // Watch for route changes to auto-collapse sidebar on clinic path
    $route(to, from) {
      this.checkRouteForCollapse();
      const hadClinicBranch = from.params.clinicUrl && from.params.branchUrl;
      const hasClinicBranch = to.params.clinicUrl && to.params.branchUrl;
      if (hasClinicBranch && !hadClinicBranch) {
        const clinicUrl = to.params.clinicUrl;
        const branchUrl = to.params.branchUrl;
        if (
          this.isAccessMenu([
            ROLES.ROLE_STOCK_STAFF,
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
          ])
        ) {
          this.fetchMrpList({ clinicUrl, branchUrl });
          this.fetchGoodsReceiveList({ clinicUrl, branchUrl });
        }
        if (this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER])) {
          this.fetchPurchaseRequests({ clinicUrl, branchUrl });
          this.fetchStockAdjustmentList({ clinicUrl, branchUrl });
        }
      }
      if (to.params.clinicUrl && this.canHqApprove) {
        this.fetchHQStockAdjustmentList({ clinicUrl: to.params.clinicUrl });
      }
    },
    // Watch for mobile sidebar state changes to update text visibility
    mobileOpen() {
      this.updateTextVisibility();
    },
  },
  beforeDestroy() {
    this.cleanupEventListeners();
    this.cleanupMobileStateObserver();
    this.clearTimers();
  },

  // Methods
  methods: {
    ...mapActions({
      fetchMrpList: "moduleMrp/fetchMrpList",
      fetchPurchaseRequests: "modulePurchaseRequest/fetchPurchaseRequests",
      fetchStockAdjustmentList:
        "moduleProductInventory/fetchStockAdjustmentList",
      fetchHQStockAdjustmentList:
        "moduleProductInventory/fetchHQStockAdjustmentList",
      fetchGoodsReceiveList: "moduleGoodsReceive/fetchGoodsReceiveList",
    }),
    goToStockManagement() {
      this.$router.push({ name: "StockManagement" });
    },
    goToPurchaseRequest() {
      this.$store.commit("modulePurchaseRequest/setFilter", "");
      this.$router.push({ name: "PurchaseRequest" });
    },
    // === Initialization Methods ===
    initializeEventListeners() {
      // Sidebar toggle event
      eventBus.$on("toggleSidebar", () => {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        this.isTemporarilyExpanded = false;
        this.clearAutoCollapseTimer();
        this.updateBodyClass();
        this.updateTextVisibility();
        eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      });

      // Document event listeners
      document.addEventListener("click", this.handleBackdropClick);
      window.addEventListener("resize", this.handleWindowResize);
    },

    initializeMobileStateObserver() {
      this.mobileStateObserver = new MutationObserver(() => {
        const newState = document.body.classList.contains(
          "mobile-sidebar-open"
        );
        this.mobileOpen = newState;
      });
      this.mobileStateObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
    },

    initializeState() {
      this.mobileOpen = document.body.classList.contains("mobile-sidebar-open");
      eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      this.updateBodyClass();
      this.updateTextVisibility();
    },

    // === Cleanup Methods ===
    cleanupEventListeners() {
      eventBus.$off("toggleSidebar");
      eventBus.$off("sidebarStateChanged");
      document.removeEventListener("click", this.handleBackdropClick);
      window.removeEventListener("resize", this.handleWindowResize);
    },

    cleanupMobileStateObserver() {
      if (this.mobileStateObserver) {
        this.mobileStateObserver.disconnect();
      }
    },

    clearTimers() {
      this.clearAutoCollapseTimer();
      if (this.textDelayTimer) {
        clearTimeout(this.textDelayTimer);
        this.textDelayTimer = null;
      }
      if (this.hoverExpandTimer) {
        clearTimeout(this.hoverExpandTimer);
        this.hoverExpandTimer = null;
      }
    },

    // === Utility Methods ===
    // Central-flow pages live under /:clinicUrl/central/... Some (e.g. the branch
    // drill-down dashboard) also carry a :branchUrl, so detect the flow by the
    // matched parent path rather than param presence.
    isCentralFlow() {
      return this.$route.matched.some((r) => /\/central(\/|$)/.test(r.path));
    },
    hasClinicBranchUrl() {
      return (
        !!(this.$route.params.clinicUrl && this.$route.params.branchUrl) &&
        !this.isCentralFlow()
      );
    },
    isCentralRoute() {
      return (
        !!this.$route.params.clinicUrl &&
        (!this.$route.params.branchUrl || this.isCentralFlow())
      );
    },
    checkActiveMenu(names) {
      if (Array.isArray(names)) {
        var active = false;
        for (var i = 0; i < names.length; i++) {
          const nameToCheck = names[i].toLowerCase
            ? names[i].toLowerCase()
            : names[i];
          const currentRouteName = this.$route.name
            ? this.$route.name.toLowerCase()
            : "";
          if (nameToCheck == currentRouteName) {
            active = true;
            break;
          }
        }
        return active;
      } else {
        const nameToCheck = names.toLowerCase ? names.toLowerCase() : names;
        const currentRouteName = this.$route.name
          ? this.$route.name.toLowerCase()
          : "";
        return nameToCheck == currentRouteName;
      }
    },

    checkRouteForCollapse() {
      if (this.$route.path === "/clinic") {
        this.sidebarCollapsed = true;
      }

      // Close mobile sidebar on route change
      if (window.innerWidth <= 768) {
        document.body.classList.remove("mobile-sidebar-open");
      }

      eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      this.updateBodyClass();
      this.updateTextVisibility();
    },

    navigateBack() {
      window.history.back();
    },

    // === State Management Methods ===
    updateBodyClass() {
      // Add or remove CSS class to body for responsive content-wrapper styling
      if (this.effectivelyCollapsed) {
        document.body.classList.add("sidebar-collapsed");
        document.body.classList.remove("sidebar-expanded");
      } else {
        document.body.classList.add("sidebar-expanded");
        document.body.classList.remove("sidebar-collapsed");
      }
    },

    updateTextVisibility() {
      if (this.textDelayTimer) {
        clearTimeout(this.textDelayTimer);
        this.textDelayTimer = null;
      }

      const isMobile = this.isMobile;

      if (isMobile) {
        if (this.mobileOpen) {
          this.textDelayTimer = setTimeout(() => {
            this.showText = true;
          }, 150);
        } else {
          this.showText = false;
        }
      } else {
        if (this.effectivelyCollapsed) {
          this.showText = false;
        } else {
          this.textDelayTimer = setTimeout(() => {
            this.showText = true;
          }, 300);
        }
      }
    },

    // === Event Handlers ===
    handleMouseEnter() {
      // Only expand on hover for desktop devices and when collapsed
      if (!this.isTouchDevice && this.sidebarCollapsed) {
        this.clearAutoCollapseTimer();

        // Add delay before expanding to prevent accidental expansion
        this.hoverExpandTimer = setTimeout(() => {
          this.isTemporarilyExpanded = true;
          this.updateBodyClass();
          this.updateTextVisibility();
          // Emit state change to navbar
          eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
        }, 500);
      }
    },
    handleMouseLeave() {
      // Clear hover expand timer if user leaves before delay completes
      if (this.hoverExpandTimer) {
        clearTimeout(this.hoverExpandTimer);
        this.hoverExpandTimer = null;
      }

      // Auto-collapse after delay for desktop devices
      if (
        !this.isTouchDevice &&
        this.sidebarCollapsed &&
        this.isTemporarilyExpanded
      ) {
        this.startAutoCollapseTimer(800); // Slightly longer delay for better UX
      }
    },
    handleTouchStart() {
      // Expand sidebar on touch for touch devices (don't toggle back)
      if (this.isTouchDevice && this.sidebarCollapsed) {
        this.clearAutoCollapseTimer();

        // Only expand if not already temporarily expanded
        if (!this.isTemporarilyExpanded) {
          this.isTemporarilyExpanded = true;
          this.updateBodyClass();
          this.updateTextVisibility();
          // Emit state change to navbar
          eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
        }

        // Touch devices: stay open until user touches outside (handled by backdrop)
      }
    },

    handleBackdropClick(event) {
      // Mobile sidebar logic
      if (
        window.innerWidth <= 768 &&
        document.body.classList.contains("mobile-sidebar-open")
      ) {
        const isHamburgerButton =
          event.target.closest('.nav-link[href="#"]') &&
          event.target.closest(".navbar-nav");

        if (isHamburgerButton) {
          return;
        }

        const sidebar = this.$el;
        if (sidebar && !sidebar.contains(event.target)) {
          document.body.classList.remove("mobile-sidebar-open");
        }
      }
      // Desktop sidebar logic - auto-collapse when expanded
      else if (window.innerWidth > 768 && !this.effectivelyCollapsed) {
        const isHamburgerButton =
          event.target.closest('.nav-link[href="#"]') &&
          event.target.closest(".navbar-nav");

        if (isHamburgerButton) {
          return;
        }

        const isMenuClick = event.target.closest(
          ".nav-link, .router-link-active, a[href], button, .btn, .nav-item"
        );
        const sidebar = this.$el;
        const isInsideSidebar = sidebar && sidebar.contains(event.target);

        if (isMenuClick || isInsideSidebar) {
          return;
        }

        if (!this.isTemporarilyExpanded) {
          this.sidebarCollapsed = true;
        } else {
          this.isTemporarilyExpanded = false;
        }

        this.clearAutoCollapseTimer();
        this.updateBodyClass();
        this.updateTextVisibility();
        eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      }
    },

    handleWindowResize() {
      this.windowWidth = window.innerWidth;

      if (window.innerWidth > 768) {
        document.body.classList.remove("mobile-sidebar-open");
        this.sidebarCollapsed = true;
        this.isTemporarilyExpanded = false;
        this.updateBodyClass();
        this.updateTextVisibility();
        eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      } else {
        document.body.classList.remove("mobile-sidebar-open");
        this.isTemporarilyExpanded = false;
        this.updateBodyClass();
        this.updateTextVisibility();
        eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      }
    },

    // === Timer Management ===
    startAutoCollapseTimer(delay) {
      this.clearAutoCollapseTimer();
      this.autoCollapseTimer = setTimeout(() => {
        this.isTemporarilyExpanded = false;
        this.updateBodyClass();
        this.updateTextVisibility();
        // Emit state change to navbar
        eventBus.$emit("sidebarStateChanged", this.effectivelyCollapsed);
      }, delay);
    },
    clearAutoCollapseTimer() {
      if (this.autoCollapseTimer) {
        clearTimeout(this.autoCollapseTimer);
        this.autoCollapseTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.nav-icon-wrapper {
  position: relative;
  display: inline-block;
}

.nav-red-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: #dd3333;
  border-radius: 50%;
}

.version-info {
  font-size: 0.7rem;
  opacity: 0.6;
}

/* Enhanced nav-link styling with minimal polish */
.component-menubar-sidebar .nav-link {
  color: var(--chrome-ink, #2c3e50) !important;
  border-radius: 6px !important;
  margin: 3px 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-weight: 500 !important;
  position: relative !important;
  overflow: hidden !important;
}

.component-menubar-sidebar .nav-link:hover {
  background: linear-gradient(
    135deg,
    var(--chrome-accent-hover-bg, rgba(66, 165, 245, 0.08)),
    var(--chrome-accent-hover-bg-2, rgba(66, 165, 245, 0.04))
  ) !important;
  color: var(--chrome-accent, #1976d2) !important;
  transform: translateX(2px) !important;
  box-shadow: 0 2px 8px var(--chrome-accent-shadow, rgba(66, 165, 245, 0.15)) !important;
}

.component-menubar-sidebar .nav-link.router-link-active {
  background: linear-gradient(135deg, var(--chrome-accent-tint, #e3f2fd), var(--chrome-accent-tint-soft, #f0f8ff)) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 6px !important;
  margin: 3px 8px !important;
  border-left: 3px solid var(--chrome-accent, #1976d2) !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px var(--chrome-accent-shadow-strong, rgba(25, 118, 210, 0.2)) !important;
}

/* Enhanced icon styling */
.component-menubar-sidebar .nav-link i {
  color: var(--chrome-muted, #546e7a) !important;
  font-size: 1.1rem !important;
  opacity: 0.85 !important;
  transition: all 0.3s ease !important;
  width: 1.2rem !important;
  text-align: center !important;
}

.component-menubar-sidebar .nav-link:hover i {
  color: var(--chrome-accent, #1976d2) !important;
  opacity: 1 !important;
  transform: scale(1.05) !important;
}

.component-menubar-sidebar .nav-link.router-link-active i {
  color: var(--chrome-accent, #1976d2) !important;
  opacity: 1 !important;
}

/* Enhanced button styling for dropdown toggles */
.component-menubar-sidebar .btn-link {
  color: var(--chrome-ink, #2c3e50) !important;
  border-radius: 6px !important;
  margin: 3px 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-weight: 500 !important;
  border: none !important;
  position: relative !important;
}

.component-menubar-sidebar .btn-link:hover {
  color: var(--chrome-accent, #1976d2) !important;
  background: linear-gradient(
    135deg,
    var(--chrome-accent-hover-bg, rgba(66, 165, 245, 0.08)),
    var(--chrome-accent-hover-bg-2, rgba(66, 165, 245, 0.04))
  ) !important;
  transform: translateX(2px) !important;
  box-shadow: 0 2px 8px var(--chrome-accent-shadow, rgba(66, 165, 245, 0.15)) !important;
}

/* Enhanced active state for dropdown buttons */
.component-menubar-sidebar .btn-link.bg-secondary {
  background: linear-gradient(135deg, var(--chrome-accent-tint, #e3f2fd), var(--chrome-accent-tint-soft, #f0f8ff)) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 6px !important;
  margin: 3px 8px !important;
  border-left: 3px solid var(--chrome-accent, #1976d2) !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px var(--chrome-accent-shadow-strong, rgba(25, 118, 210, 0.2)) !important;
}

.component-menubar-sidebar .btn-link.bg-secondary:hover {
  background-color: var(--chrome-accent-tint-strong, #bbdefb) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
}

/* Active state icons for dropdown buttons */
.component-menubar-sidebar .btn-link.bg-secondary i {
  color: var(--chrome-accent, #1976d2) !important;
}

/* Collapse button styling */
.btn-link:focus {
  box-shadow: none;
}

/* Enhanced sidebar container */
.component-menubar-sidebar {
  overflow-x: hidden;
  background: linear-gradient(180deg, var(--chrome-surface, #ffffff) 0%, var(--chrome-surface-2, #fafbfc) 100%) !important;
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.08) !important;
}

/* Enhanced default nav-link styling */
.component-menubar-sidebar .nav-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.65rem 1rem;
  min-height: 2.8rem;
  line-height: 1.4;
}

/* This section is now handled by enhanced icon styling above */

/* When sidebar is collapsed on desktop only, center content but maintain icon size */
@media (min-width: 769px) {
  body.sidebar-collapsed .component-menubar-sidebar .nav-link {
    justify-content: center;
    padding: 0.5rem 0.25rem;
    /* Reduce horizontal padding for better centering */
    margin: 2px 4px !important;
    /* Reduce horizontal margins for collapsed state */
  }

  /* Ensure buttons also get proper collapsed styling */
  body.sidebar-collapsed .component-menubar-sidebar .btn-link {
    justify-content: center;
    padding: 0.5rem 0.25rem;
    margin: 2px 4px !important;
  }

  /* Active states in collapsed mode - maintain centering */
  body.sidebar-collapsed .component-menubar-sidebar .nav-link.bg-secondary,
  body.sidebar-collapsed
    .component-menubar-sidebar
    .nav-link.router-link-active,
  body.sidebar-collapsed .component-menubar-sidebar .btn-link.bg-secondary {
    justify-content: center;
    margin: 2px 4px !important;
    padding: 0.5rem 0.25rem;
  }

  /* Collapsed navbar brand centering */
  body.sidebar-collapsed .component-menubar-sidebar .navbar-brand {
    justify-content: center;
    padding: 1rem 0.5rem;
  }
}

/* Ensure collapsed active items maintain rounded corners on all screen sizes */
body.sidebar-collapsed .component-menubar-sidebar .nav-link.bg-secondary,
body.sidebar-collapsed .component-menubar-sidebar .nav-link.router-link-active,
body.sidebar-collapsed .component-menubar-sidebar .btn-link.bg-secondary {
  border-radius: 8px !important;
}

/* Mobile sidebar - always left-aligned and high z-index */
@media (max-width: 768px) {
  .component-menubar-sidebar {
    z-index: 1000 !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    height: 100vh !important;
    width: 250px !important;
  }

  .component-menubar-sidebar .nav-link,
  .component-menubar-sidebar .btn-link {
    justify-content: flex-start !important;
    padding: 0.5rem 1rem !important;
    margin: 2px 8px !important;
  }

  .component-menubar-sidebar .navbar-brand {
    justify-content: flex-start !important;
    padding: 1rem !important;
  }

  /* Consistent icon spacing on mobile */
  .component-menubar-sidebar .nav-link i,
  .component-menubar-sidebar .btn-link i {
    margin-right: 0.5rem !important;
  }

  /* Ensure mobile sidebar is above everything */
  body.mobile-sidebar-open .component-menubar-sidebar {
    z-index: 1001 !important;
    transform: translateX(0) !important;
  }
}

/* Active state highlighting for nav items - Modern rounded corners */
.component-menubar-sidebar .nav-item.bg-secondary,
.component-menubar-sidebar .nav-item.bg-secondary .nav-link,
.component-menubar-sidebar .nav-link.bg-secondary,
.component-menubar-sidebar .text-dark.bg-secondary {
  background-color: var(--chrome-accent-tint, #e3f2fd) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
  margin: 2px 8px !important;
}

.component-menubar-sidebar .nav-item.bg-secondary:hover,
.component-menubar-sidebar .nav-item.bg-secondary .nav-link:hover,
.component-menubar-sidebar .nav-link.bg-secondary:hover,
.component-menubar-sidebar .text-dark.bg-secondary:hover {
  background-color: var(--chrome-accent-tint-strong, #bbdefb) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
}

/* Active state icons for bg-secondary items - Multiple selectors */
.component-menubar-sidebar .nav-item.bg-secondary i,
.component-menubar-sidebar .nav-item.bg-secondary .nav-link i,
.component-menubar-sidebar .nav-link.bg-secondary i,
.component-menubar-sidebar .text-dark.bg-secondary i {
  color: var(--chrome-accent, #1976d2) !important;
}

/* Bootstrap Vue specific overrides - Modern rounded corners */
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary,
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary a,
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary .nav-link {
  background-color: var(--chrome-accent-tint, #e3f2fd) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
  margin: 2px 8px !important;
}

.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary:hover,
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary a:hover,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.bg-secondary
  .nav-link:hover {
  background-color: var(--chrome-accent-tint-strong, #bbdefb) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
}

/* Bootstrap Vue icon overrides */
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary i,
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary a i,
.component-menubar-sidebar .nav.flex-column .nav-item.bg-secondary .nav-link i {
  color: var(--chrome-accent, #1976d2) !important;
}

/* Ultimate override - Modern rounded corners with maximum specificity */
.component-menubar-sidebar .nav.flex-column .nav-item.text-dark.bg-secondary,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  a.nav-link,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  .nav-link.text-dark {
  background-color: var(--chrome-accent-tint, #e3f2fd) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
  margin: 2px 8px !important;
}

.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary:hover,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  a.nav-link:hover,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  .nav-link.text-dark:hover {
  background-color: var(--chrome-accent-tint-strong, #bbdefb) !important;
  color: var(--chrome-active-ink, #1976d2) !important;
  border-radius: 8px !important;
}

.component-menubar-sidebar .nav.flex-column .nav-item.text-dark.bg-secondary i,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  a.nav-link
  i,
.component-menubar-sidebar
  .nav.flex-column
  .nav-item.text-dark.bg-secondary
  .nav-link.text-dark
  i {
  color: var(--chrome-accent, #1976d2) !important;
}

/* Ensure popover links are properly styled */
.component-menubar-sidebar .popover .text-dark {
  color: var(--chrome-ink, #333) !important;
}

.component-menubar-sidebar .popover .text-dark:hover {
  color: var(--chrome-accent, #007bff) !important;
  text-decoration: underline;
}

/* Enhanced text animation with smoother transitions */
.component-menubar-sidebar span {
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-8px);
}

/* Smooth fade-in with slide effect */
.component-menubar-sidebar span[style*="display: block"],
.component-menubar-sidebar span:not([style*="display: none"]) {
  opacity: 1;
  transform: translateX(0);
}

/* Enhanced version info styling */
.version-info {
  font-size: 0.7rem;
  opacity: 0.5;
  padding: 0.5rem;
  text-align: center;
  color: var(--chrome-muted, #78909c) !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  transition: opacity 0.3s ease !important;
}

.version-info:hover {
  opacity: 0.8;
}

/* Enhanced brand text styling */
.brand-text {
  font-weight: 700 !important;
  font-size: 1.3rem !important;
  color: rgb(56, 104, 156) !important;
  letter-spacing: 0.5px !important;
  font-family: "Noto Sans Thai", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif !important;
  text-shadow: 0 1px 2px rgba(25, 118, 210, 0.1) !important;
  position: relative !important;
  top: 2px !important;
  transition: all 0.3s ease !important;
}

/* Disabled menu item styling with maximum specificity */
.component-menubar-sidebar .nav.flex-column .nav-item.disabled-menu-item,
.component-menubar-sidebar .nav.flex-column .nav-item.disabled-menu-item:hover,
.component-menubar-sidebar .nav.flex-column .nav-item.disabled-menu-item:focus {
  color: #6c757d !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  background: transparent !important;
  transform: none !important;
  box-shadow: none !important;
}
</style>
